import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { devicesAPI, childAPI } from '../api/client';
import { StatCard, Card, EmptyState, Loading } from '../components/UI';
import T from '../theme';

// ===== ADMIN DASHBOARD =====
function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    devicesAPI.stats().then(r => setStats(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <Loading />;

  return (
    <div>
      <div style={{
        background: `linear-gradient(135deg, ${T.blue}, ${T.blueLight})`,
        borderRadius: 20, padding: '20px 24px', marginBottom: 24,
        color: '#fff', boxShadow: `0 8px 32px ${T.blue}30`,
      }}>
        <h1 style={{ fontSize: 22, fontWeight: 900, margin: '0 0 8px' }}>🛡 Admin Panel</h1>
        <p style={{ fontSize: 15, opacity: 0.9, margin: 0 }}>SafeChild qurilmalarni boshqarish</p>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 24 }}>
        <StatCard icon="📡" label="Jami" value={stats?.total_devices || 0} color={T.blue} bg={T.blueSoft} border={T.blueBorder} />
        <StatCard icon="✨" label="Yangi" value={stats?.new || 0} color="#92400E" bg={T.yellowSoft} border="#FCD34D" />
        <StatCard icon="✅" label="Faol" value={stats?.active || 0} color="#065F46" bg={T.greenSoft} border="#6EE7B7" />
        <StatCard icon="⚠️" label="Nofaol" value={stats?.inactive || 0} color="#991B1B" bg={T.coralSoft} border="#FCA5A5" />
      </div>

      {/* Quick actions */}
      <Card>
        <h3 style={{ fontSize: 16, fontWeight: 800, color: T.text, margin: '0 0 16px' }}>⚡ Tezkor harakatlar</h3>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <button onClick={() => navigate('/admin/devices?action=add')} style={{
            padding: '14px 24px', background: T.blueSoft, border: `2px solid ${T.blueBorder}`,
            borderRadius: 14, fontSize: 14, fontWeight: 700, color: T.blue,
            cursor: 'pointer', fontFamily: T.font, display: 'flex', alignItems: 'center', gap: 8,
          }}>📡 Yangi qurilma qo'shish</button>
          <button onClick={() => navigate('/admin/devices')} style={{
            padding: '14px 24px', background: '#fff', border: `2px solid ${T.border}`,
            borderRadius: 14, fontSize: 14, fontWeight: 700, color: T.textSec,
            cursor: 'pointer', fontFamily: T.font, display: 'flex', alignItems: 'center', gap: 8,
          }}>📋 Qurilmalar ro'yxati</button>
        </div>
      </Card>
    </div>
  );
}

// ===== PARENT DASHBOARD =====
function ParentDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    childAPI.list().then(r => setChildren(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const getStatusInfo = (s) => {
    if (!s) return { label: "Noma'lum", color: T.textMuted, bg: '#F1F5F9', bc: T.border };
    if (s === 'at_home') return { label: 'Uyda', color: '#065F46', bg: '#ECFDF5', bc: '#6EE7B7' };
    if (s === 'at_school') return { label: 'Maktabda', color: T.blue, bg: T.blueSoft, bc: T.blueBorder };
    if (s.startsWith('at_zone')) { const n = s.split(':')[2]; return { label: n || 'Hududda', color: '#7C3AED', bg: '#F5F3FF', bc: '#C4B5FD' }; }
    if (s === 'on_road') return { label: "Yo'lda", color: '#92400E', bg: T.yellowSoft, bc: '#FCD34D' };
    return { label: "Signal yo'q", color: '#991B1B', bg: T.coralSoft, bc: '#FCA5A5' };
  };

  if (loading) return <Loading />;

  return (
    <div>
      {/* Welcome banner */}
      <div style={{
        background: `linear-gradient(135deg, ${T.blue}, ${T.blueLight})`,
        borderRadius: 20, padding: '20px 24px', marginBottom: 24,
        color: '#fff', boxShadow: `0 8px 32px ${T.blue}30`,
      }}>
        <h1 style={{ fontSize: 20, fontWeight: 900, margin: '0 0 6px' }}>
          Assalomu alaykum, {user?.first_name || user?.username}! 👋
        </h1>
        <p style={{ fontSize: 15, opacity: 0.9, margin: 0 }}>Bolalaringiz xavfsizligini kuzating</p>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 14, marginBottom: 24 }}>
        <StatCard icon="👶" label="Bolalar" value={children.length} color={T.blue} bg={T.blueSoft} border={T.blueBorder} />
        <StatCard icon="📡" label="Faol" value={children.filter(c => c.device_info?.status === 'active').length} color="#065F46" bg="#ECFDF5" border="#6EE7B7" />
        <StatCard icon="⚠️" label="Signal yo'q" value={children.filter(c => !c.current_location).length} color="#92400E" bg={T.yellowSoft} border="#FCD34D" />
      </div>

      {/* Children list */}
      <Card padding={0}>
        <div style={{
          padding: '16px 20px', borderBottom: `1px solid ${T.border}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <h2 style={{ fontSize: 16, fontWeight: 800, color: T.text, margin: 0 }}>👶 Bolalar</h2>
          <button onClick={() => navigate('/children/add')} style={{
            padding: '8px 16px', background: T.blue, color: '#fff',
            border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700,
            cursor: 'pointer', fontFamily: T.font,
          }}>＋ Qo'shish</button>
        </div>

        {children.length === 0 ? (
          <div style={{ padding: '50px 20px', textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 12, opacity: 0.3 }}>👶</div>
            <p style={{ color: T.textMuted, fontWeight: 600 }}>Hali bolalar qo'shilmagan</p>
            <button onClick={() => navigate('/children/add')} style={{
              marginTop: 16, padding: '12px 24px',
              background: `linear-gradient(135deg, ${T.blue}, ${T.blueLight})`,
              color: '#fff', border: 'none', borderRadius: 12,
              fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: T.font,
            }}>+ Bola qo'shish</button>
          </div>
        ) : (
          <div>
            {children.map(c => {
              const st = getStatusInfo(c.location_status);
              return (
                <div
                  key={c.id}
                  onClick={() => navigate(`/children/${c.id}`)}
                  style={{
                    padding: '16px 20px', borderBottom: `1px solid ${T.borderLight}`,
                    display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer',
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = '#F8FAFC'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <div style={{
                    width: 48, height: 48, borderRadius: 14,
                    background: `linear-gradient(135deg, ${T.blue}, ${T.blueLight})`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 20, color: '#fff', fontWeight: 900,
                  }}>{c.name?.[0]?.toUpperCase()}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 15, fontWeight: 800, color: T.text }}>{c.name}</div>
                    <div style={{ fontSize: 13, color: T.textMuted, marginTop: 2 }}>
                      {c.age ? `${c.age} yosh · ` : ''}{c.child_type_display}
                    </div>
                  </div>
                  <span style={{
                    padding: '5px 14px', borderRadius: 20, fontSize: 12, fontWeight: 700,
                    color: st.color, background: st.bg, border: `1px solid ${st.bc}`,
                  }}>{st.label}</span>
                  <span style={{ color: T.textMuted, fontSize: 16 }}>→</span>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}

// ===== MAIN DASHBOARD =====
export default function DashboardPage() {
  const { isAdmin } = useAuth();
  return isAdmin ? <AdminDashboard /> : <ParentDashboard />;
}
